/////React/////
import React from 'react';
/////Material UI/////
import {Stack, Box, Typography, Link, Breadcrumbs, Grid, LinearProgress} from '@mui/material';

/////Local types and images/////
import {
  DefaultIProcess,
  LogoSize,
  ZoomActions,
  PinTypes,
  ProcessTabs,
  ChangePageType,
  Custom_Encode_URL,
  Custom_Decode_URL,
  DocumentCategories,
  E_QuickLinks,
} from 'app/types/Shared';
import smallLogo from 'assets/images/header/elevateLogoSmall.svg';
import takedaLogo from 'assets/images/header/takedaLogo.svg';
//////Store/////
import {
  setSelectedProcess,
  selectSelectedProcess,
  setSelectedSubProcess,
  setSelectedL1Label,
  selectSubProcess,
  setSelectedL2Label,
  selectL2Label,
  selectSelectedL1Label,
  selectOnboarding,
  selectMapZoom,
  setMapZoom,
  selectProcessPageTab,
  setProcessPageTab,
  selectProcessPageTabIndex,
  setProcessPageTabIndex,
  setSelectedProcessSteps,
  setSelectedProcessMapNodes,
  selectSelectedProcessSteps,
  selectSelectedProcessMapNodes,
  selectSimplifiedRACI,
  setSimplifiedRACI,
  selectL2Reset,
  setL2Reset,
  setSelectedGroup,
  setSelectedParentOpen,
  selectParentOpen,
  selectDefaultZoom,
  selectCurrentMapXL,
  setDefaultZoom,
} from 'app/store/appState/appStateSlice';
import {selectAllProcesses, selectAllMaps} from 'app/store/operatingModel/operatingModelSlice';
import {selectUser, selectRecentlyViewed, setRecentlyViewed} from 'app/store/users/userSlice';
import {useAddUserUrlPinMutation, useGetUserByEmailQuery, useRemoveUserPinMutation} from 'app/store/users/userApiSlice';
import {useGetProcessStepsByIdQuery} from 'app/store/operatingModel/operatingModelApi';
import {useDispatch, useSelector} from 'react-redux';
/////React-Router-Dom/////
import {NavLink, useNavigate, useParams} from 'react-router-dom';
/////Local Components/////
import PageLayout from 'components/PageLayout/PageLayout';
import TopNavigation from 'components/TopNavigation/TopNavigation';
import RACIView from 'components/RACITable/RACIView';
// import Navigator from 'components/Navigator/Navigator';
import Abbreviation from 'components/Abbreviation/Abbreviation';
import ProcessDetailsPage from 'components/ProcessDetailsPage/ProcessDetailsPage';
import SubProcessNavigation from 'components/SubProcessNavigation/SubProcessNavigation';
import FlowChartL2 from 'components/FlowChart/L2/FlowChartL2';
import ChangeRole from 'components/ChangeRole/ChangeRole';
import {selectUserProfile} from 'app/store/auth/authSlice';
import {useMatomo} from '@jonkoops/matomo-tracker-react';
import DocAndTemplatesTab from 'components/ProcessDetailsPage/DocsAndTemplates';
import TabPanel from 'components/ProcessDetailsPage/TabPanel';
import ProcessUpdates from 'components/ProcessUpdates/ProcessUpdates';
import {useFeatureFlag} from 'configcat-react';

const ProcessPage = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {processSlug} = useParams<{processSlug?: string}>();

  const user = useSelector(selectUser);
  const authUser = useSelector(selectUserProfile);
  const onboarding = useSelector(selectOnboarding);
  const allProcesses = useSelector(selectAllProcesses);
  const allMaps = useSelector(selectAllMaps);

  const selectedProcess = useSelector(selectSelectedProcess);
  const processStepsResult = useGetProcessStepsByIdQuery(selectedProcess ? Number(selectedProcess.id) : 0, {
    skip: !authUser.token,
  });
  const selectedProcessSteps = useSelector(selectSelectedProcessSteps);
  const selectedMapNodes = useSelector(selectSelectedProcessMapNodes);
  const selectedSubProcess = useSelector(selectSubProcess);
  const selectedL1Label = useSelector(selectSelectedL1Label);
  const selectedL2Label = useSelector(selectL2Label);
  const selectedParentsOpen = useSelector(selectParentOpen);
  const processTab = useSelector(selectProcessPageTab);
  const processTabIndex = useSelector(selectProcessPageTabIndex);
  const mapZoom = useSelector(selectMapZoom);
  const defaultZoom = useSelector(selectDefaultZoom);
  const isMapXL = useSelector(selectCurrentMapXL);
  const recentlyViewed = useSelector(selectRecentlyViewed);
  const simplifiedRACI = useSelector(selectSimplifiedRACI);
  const l2Reset = useSelector(selectL2Reset);

  const {value: isChangeNotificationsEnabled} = useFeatureFlag('feature_change_notifications', false);

  //returns user email
  const getUserEmail = () => {
    if (user?.email) {
      return user.email;
    } else if (authUser.userProfile?.email) {
      return authUser.userProfile?.email;
    } else return ' ';
  };

  // Matomo Analytics
  const {trackPageView, trackEvent} = useMatomo();
  React.useEffect(() => {
    trackPageView();
  });

  //user by email (refetch if pins/unpins page)
  const {refetch} = useGetUserByEmailQuery(getUserEmail(), {skip: getUserEmail() == ' ' || !authUser.token});
  const [createProcessArtifact] = useAddUserUrlPinMutation();
  const [userRemovePin] = useRemoveUserPinMutation();

  //Callback for TopNavigation Logo/Home Button
  function navigateHome() {
    navigate('/home');
    return Promise.resolve();
  }

  const onLogout = async () => {
    navigate('/logout');
    return Promise.resolve();
  };

  const onSearch = (query?: string) => {
    navigate(`/search?q=${query}`);
    return Promise.resolve();
  };

  const toResources = async () => {
    navigate('/resources');
    return Promise.resolve();
  };

  const toArtifactsPage = async () => {
    navigate('/resources/process-documents');
    return Promise.resolve();
  };

  //Initalizaes the process page -- fetches new operating model if needed, sets diagrams & sets filtered raci steps
  React.useEffect(() => {
    const process_OM = selectedProcess?.operating_models_list;
    if (process_OM) {
      const diagram = allMaps?.find((x) => x.diagram_id === selectedProcess?.diagram_link);
      if (diagram) {
        dispatch(setSelectedSubProcess(diagram.l1_label_order));
        dispatch(setSelectedL1Label(diagram.l1_label));
        if (diagram.l2_label) {
          dispatch(setSelectedL2Label(diagram.l2_label));
        }
      }
    }

    if (processStepsResult.isSuccess && processStepsResult.data) {
      if (processStepsResult.data.steps) {
        dispatch(setSelectedProcessSteps(processStepsResult.data.steps));
      }
      if (processStepsResult.data.map) {
        dispatch(setSelectedProcessMapNodes(processStepsResult.data.map.process_map_node));
      }
    }
    //TTDOM processes default to RACI view
    if (selectedProcess?.operating_models_list === 'TTDOM' && processTab === ProcessTabs.Map) {
      dispatch(setProcessPageTab(ProcessTabs.RACI));
    }
  }, [dispatch, selectedProcess, allMaps, processStepsResult, authUser.token, processTab]);

  //New Process click (set selcted process, maps & filtered steps)
  const setNewProcess = (parent_process: string) => {
    const parentProcess = allProcesses?.find((x) => x.process_id === parent_process);
    if (parentProcess) {
      dispatch(setSelectedProcess(parentProcess));
      // if (steps) dispatch(setFilteredSteps(steps.filter((step) => step.step.process_key === parentProcess.name)));
      if (parentProcess.diagram_link) {
        const diagram = allMaps?.find((x) => x.diagram_id === parentProcess.diagram_link);
        if (diagram) {
          dispatch(setSelectedSubProcess(-1));
          dispatch(setSelectedL1Label(diagram.l1_label));
          if (diagram.l2_label) {
            dispatch(setSelectedL2Label(diagram.l2_label));
          }
        }
      }
      const url = `/process/${Custom_Encode_URL(parentProcess.name)}/${parentProcess.operating_models_list}`;
      navigate(url);
    }
  };

  //Click back to HomePage
  const handleLinkClick = (parent_process?: string) => {
    if (parent_process) {
      setNewProcess(parent_process);
      trackEvent({category: 'Process', action: 'Parent Process Selected', name: parent_process});
    } else {
      dispatch(setSelectedProcess(DefaultIProcess));
      dispatch(setProcessPageTab(ProcessTabs.Map));
      dispatch(setSelectedL2Label(''));
      dispatch(setSelectedL1Label(''));
      navigate('/home');
    }
  };

  //Use Slug to set selected process
  React.useEffect(() => {
    const processName = Custom_Decode_URL(processSlug)?.toLowerCase();
    if (selectedProcess?.name.toLowerCase().replaceAll('-', ' ') !== processName) {
      if (allProcesses?.length) {
        const process = allProcesses.find(
          (x) =>
            x.name.toLowerCase().replaceAll('-', ' ') === processName &&
            x.diagram_link != null &&
            x.operating_models_list === params.modelSlug,
        );
        if (process) {
          dispatch(setSelectedProcess(process));
        } else {
          const process = allProcesses.find((x) => x.name.toLowerCase().replaceAll('-', ' ') === processName);
          if (process) {
            dispatch(setSelectedProcess(process));
          } else {
            navigate('/404');
          }
        }
      }
    }

    if (selectedProcess?.name) {
      // Add recently viewed if the state is uninitialized or already visited
      const addRecentlyViewed =
        !recentlyViewed || (recentlyViewed && recentlyViewed.at(0)?.key !== selectedProcess?.process_id);

      if (addRecentlyViewed) {
        const idx = window.location.pathname.replace('/process/', '').lastIndexOf('/');
        if (idx !== -1) {
          const title = Custom_Decode_URL(window.location.pathname.replace('/process/', '').substring(0, idx));
          if (title === selectedProcess?.name.replaceAll('-', ' ')) {
            dispatch(
              setRecentlyViewed({
                id: 0,
                title: selectedProcess?.name,
                url: window.location.href,
                type: PinTypes.Process,
                created_dt: selectedProcess.created_dt,
                key: selectedProcess.process_id,
              }),
            );
          }
        }
      }
    }
  });

  const renderReady =
    selectedProcess?.operating_models_list !== 'TTDOM'
      ? selectedProcess?.operating_models_list === params.modelSlug &&
        selectedProcess &&
        allMaps &&
        selectedMapNodes &&
        (!selectedProcess.diagram_link ||
          (selectedMapNodes.length > 0 && selectedMapNodes[0]?.process_id == selectedProcess.id))
      : selectedProcess?.operating_models_list === params.modelSlug &&
        selectedProcess &&
        allMaps &&
        selectedProcessSteps &&
        selectedProcessSteps.length > 0 &&
        selectedProcessSteps[0]?.process_key == selectedProcess.process_id;

  const prevMapZoom = React.useRef(mapZoom);
  const prevDefaultMapZoom = React.useRef(defaultZoom);
  // Header scroll & start at top of page
  const [onFirstLoad, setFirstLoad] = React.useState(true);
  React.useEffect(() => {
    if (onFirstLoad) {
      window.scrollTo(0, 0);
      setFirstLoad(false);
    }
  }, [onFirstLoad]);
  // //Scroll Map/Raci On Page load/refresh
  React.useEffect(() => {
    // Check if `mapZoom` has changed
    if (isMapXL && defaultZoom !== prevDefaultMapZoom.current) {
      prevDefaultMapZoom.current = defaultZoom;
      return;
    }
    if (!isMapXL && mapZoom !== prevMapZoom.current) {
      // Skip the effect when `mapZoom` changes
      prevMapZoom.current = mapZoom;
      return;
    }
    const scrollToElement = async () => {
      //wait for map nodes to render in document
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (processTab === ProcessTabs.Map && selectedL2Label) {
        const index = selectedL2Label.indexOf(' ');
        let newId = selectedL2Label.substring(0, index);
        newId += '-group-node';
        const scrollElement = document.getElementById(newId);
        if (scrollElement) {
          if ((isMapXL && defaultZoom > 1) || (!isMapXL && mapZoom > 1)) {
            const containerElement = document.getElementById('scroll-paper-container');
            if (containerElement) {
              // Calculate the element's offset relative to the container
              const elementOffset =
                scrollElement.getBoundingClientRect().left - containerElement.getBoundingClientRect().left;
              // Calculate the current scroll position of the container
              const currentScrollLeft = containerElement.scrollLeft;

              // Calculate the target scroll position to bring the element into view
              const targetScrollLeft = currentScrollLeft + elementOffset;
              // Scroll both the container and the element into view
              containerElement.scrollTo({left: targetScrollLeft, behavior: 'auto'});
            }
          } else {
            scrollElement.scrollIntoView({behavior: 'auto', block: 'nearest', inline: 'start'});
          }
        }
      }
      if (processTab === ProcessTabs.RACI && selectedL2Label) {
        const scrollElement = document.getElementById(selectedL2Label);
        if (scrollElement) {
          scrollElement.scrollIntoView({behavior: 'auto', block: 'start', inline: 'nearest'});
          window.scrollTo(0, 50);
        }
      }
      prevMapZoom.current = mapZoom;
    };
    if (renderReady) {
      scrollToElement();
    }
  }, [
    processTab,
    selectedL2Label,
    prevMapZoom,
    mapZoom,
    renderReady,
    l2Reset,
    isMapXL,
    prevDefaultMapZoom,
    defaultZoom,
  ]);

  const header = (
    <TopNavigation
      authorized={true}
      logout={onLogout}
      smallLogo={smallLogo}
      rightLogo={takedaLogo}
      LogoType={LogoSize.Small}
      home={navigateHome}
      search={onSearch}
      toResources={toResources}
      banner={onboarding.topBanner}
      ttdom={selectedProcess?.operating_models_list === 'TTDOM'}
    />
  );

  const footer = <></>;
  const sidebar = {
    //navigatorContent: <Navigator onExplore={onNavigatorClick} />,
    legendContent: <Abbreviation />,
  };

  //Get data for SubProcessHeaders
  const getSubHeaderData = () => {
    if (processTab === ProcessTabs.RACI) {
      const HeadersByLevel = selectedProcessSteps?.reduce((l1, curr) => {
        if (curr.level === 1 && curr.order) {
          if (curr.level_label) {
            l1.push({title: curr.level_label, order: l1?.length + 1, l2: []});
          }
        } else if (curr.level === 2 && l1.length > 0) {
          if (curr.level_label) {
            l1[l1.length - 1].l2.push({key: l1.length, label: curr.level_label});
          }
        }
        return l1;
      }, [] as Array<{title: string; order: number; l2: Array<{key: number; label: string}>}>);

      return HeadersByLevel;
    } else {
      const mapByProcess = allMaps?.filter((map) => map.process_name === selectedProcess?.process_id);
      const HeadersByLevel = mapByProcess?.reduce((l1, curr) => {
        if (l1.findIndex((x) => curr.l1_label === x.title) === -1) {
          l1.push({title: curr.l1_label, order: curr.l1_label_order, l2: []});
          if (curr.diagram_anchor !== null) {
            if (curr.l2_label) l1[l1.length - 1].l2.push({key: curr.l1_label_order, label: curr.l2_label});
          }
        } else if (curr.diagram_anchor !== null) {
          if (curr.l2_label) l1[l1.length - 1].l2.push({key: curr.l1_label_order, label: curr.l2_label});
        }

        return l1;
      }, [] as Array<{title: string; order: number; l2: Array<{key: number; label: string}>}>);

      return HeadersByLevel;
    }
  };

  const onL1Click = async (subprocess: number, label: string) => {
    trackEvent({category: 'Process', action: 'Sub-Process Selected', name: label, value: subprocess});
    dispatch(setSelectedSubProcess(subprocess));
    dispatch(setSelectedL1Label(label));
  };

  const pinProcessPage = async (add: boolean) => {
    const userId = user?.id;
    const url = window.location.href;
    if (userId && add && selectedProcess?.process_id) {
      trackEvent({category: 'User Pin', action: 'Process Pin Created', name: selectedProcess.label});

      await createProcessArtifact({userId: userId, url: url, key: selectedProcess.process_id, type: PinTypes.Process});
      await refetch();
    } else if (userId && !add) {
      const userPin = user?.user_pin.find((pin) => pin.object_url === window.location.href);
      if (userPin) {
        trackEvent({category: 'User Pin', action: 'Pin Deleted', name: userPin.object_id});

        await userRemovePin({userId: userPin.user_id, pinId: userPin.id, key: userPin.object_id});
        await refetch();
      }
    }
  };

  const getProcessPinned = () => {
    if (user?.user_pin) {
      const userPin = user?.user_pin.some((pin) => pin.object_id === selectedProcess?.process_id);
      return userPin;
    } else return false;
  };

  // Click to Raci/Process Map Button
  const HandleViewChange = (route: ProcessTabs) => {
    if (route === ProcessTabs.RACI) {
      const l1_label = selectedProcessSteps?.find(
        (x) => x.level === 1 && x.desc === selectedL1Label && x.process_key === selectedProcess?.process_id,
      );
      if (l1_label && l1_label.level_label) {
        trackEvent({category: 'Process', action: 'RACI View Opened', name: l1_label.level_label});
      }
    } else if (route === ProcessTabs.Map) {
      const l1_label = allMaps?.find(
        (x) => x.l1_label === selectedL1Label && x.process_name === selectedProcess?.process_id,
      );
      if (l1_label) {
        trackEvent({category: 'Process', action: 'Map View Opened', name: l1_label.l1_label});
        dispatch(setSelectedSubProcess(l1_label.l1_label_order));
      } else {
        const diagram = allMaps?.find((x) => x.diagram_id === selectedProcess?.diagram_link);
        if (diagram) {
          dispatch(setSelectedSubProcess(diagram.l1_label_order));
          dispatch(setSelectedL1Label(diagram.l1_label));
        }
      }
    }
  };

  const onSearchShortcut = (query: string, filter?: DocumentCategories) => {
    navigate(`/search?q=${query}&filter=${filter}`);
    return Promise.resolve();
  };

  const findProcess = (parent: string) => {
    const processName = allProcesses?.find((x) => x.process_id === parent);
    if (processName) {
      return processName.name;
    } else return parent;
  };

  const handleProcessPageTabChange = (tabIndex: number, tab: ProcessTabs) => {
    trackEvent({category: 'Process', action: 'Map View Changed', name: selectedL2Label});

    dispatch(setProcessPageTabIndex(tabIndex));
    dispatch(setProcessPageTab(tab));
    if (tab === ProcessTabs.RACI) {
      HandleViewChange(ProcessTabs.RACI);
    } else if (tab === ProcessTabs.Map) {
      HandleViewChange(ProcessTabs.Map);
    }
  };

  //Callback for L2 Header Click
  const onL2Click = async (id: string) => {
    if (id === selectedL2Label) {
      dispatch(setL2Reset(!l2Reset));
    }
    dispatch(setSelectedL2Label(id));
  };

  const setSimplified = (simplify: boolean) => {
    if (simplify) {
      //track user engagement with simplified RACI
      console.log('simplifed RACI selected');
      trackEvent({
        category: 'Process',
        action: 'Simplified RACI Selected',
        name: selectedProcess?.name,
      });
    }
    dispatch(setSimplifiedRACI(simplify));
  };

  return (
    <>
      {renderReady ? (
        <PageLayout header={header} footer={footer} sidebar={sidebar} globalBannerCallback={onLogout}>
          <Grid
            marginTop={onboarding?.topBanner ? 7.5 : 2.5}
            justifyContent='flex-start'
            alignItems='flex-end'
            container
            direction='row'
            spacing={1}
            px={6}
          >
            <Grid item xs={8.5}>
              <Breadcrumbs
                sx={{
                  '.MuiBreadcrumbs-separator': {
                    color: 'primary.main',
                  },
                  marginTop: selectedProcess?.operating_models_list === 'TTDOM' ? 8 : 0,
                }}
              >
                <Link
                  underline='hover'
                  color='primary.main'
                  component={NavLink}
                  to='/home'
                  onClick={() => handleLinkClick()}
                >
                  Home
                </Link>
                {selectedProcess?.parent_process &&
                  findProcess(selectedProcess.parent_process) !== selectedProcess.name && (
                    <Link
                      underline='hover'
                      color='primary.main'
                      onClick={() => {
                        dispatch(setSelectedGroup(selectedProcess.category_group));
                        selectedProcess?.parent_process_id &&
                          !selectedParentsOpen?.includes(selectedProcess.parent_process_id) &&
                          dispatch(setSelectedParentOpen(selectedProcess.parent_process_id));
                        navigateHome();
                      }}
                    >
                      {findProcess(selectedProcess?.parent_process)}
                    </Link>
                  )}
                {selectedProcess?.operating_models_list === 'TTDOM' && (
                  <Link
                    underline='hover'
                    color='primary.main'
                    onClick={() => {
                      dispatch(setSelectedGroup(E_QuickLinks.TDOM));
                      navigate('/home');
                    }}
                  >
                    TTDOM Processes
                  </Link>
                )}
                <Typography color='primary.main'>{selectedProcess?.name}</Typography>
              </Breadcrumbs>
            </Grid>
            <Grid item xs={3.5} minHeight='70px' display='flex' justifyContent='flex-end'>
              <ChangeRole />
            </Grid>
          </Grid>
          <Stack direction='column' spacing={4}>
            {selectedProcess && (
              <Box paddingBottom={2} pt={1}>
                <ProcessDetailsPage
                  process={selectedProcess}
                  setPinCallback={pinProcessPage}
                  pinned={getProcessPinned()}
                  onSearch={onSearchShortcut}
                  veiwAllCallback={toArtifactsPage}
                  changeTabs={handleProcessPageTabChange}
                  currentTab={processTabIndex}
                  navigateToProcess={setNewProcess}
                >
                  {isChangeNotificationsEnabled && (
                    <TabPanel
                      tabId={ProcessTabs.Overview}
                      index={0}
                      value={processTabIndex}
                      horizontalPadding={0}
                      backgroundColor='rgb(217, 217, 217, 0.2)'
                      maxWidth='100vw'
                      minWidth='99vw'
                    >
                      <ProcessUpdates
                        type={ChangePageType.ProcessPage}
                        title='Overview'
                        description={selectedProcess.desc}
                        subtitle={`Updates to ${selectedProcess.name}`}
                        px={48}
                      />
                    </TabPanel>
                  )}
                  {selectedProcess?.diagram_link && (
                    <TabPanel
                      tabId={ProcessTabs.Map}
                      index={isChangeNotificationsEnabled ? 1 : 0}
                      value={processTabIndex}
                      horizontalPadding={0}
                      backgroundColor='rgb(217, 217, 217, 0.2)'
                      maxWidth='100vw'
                      minWidth='99vw'
                    >
                      <SubProcessNavigation
                        data={getSubHeaderData()}
                        onL1Click={onL1Click}
                        selectedL1Label={selectedL1Label}
                        selectedL2Label={selectedL2Label}
                        onL2Click={onL2Click}
                        mode={ProcessTabs.Map}
                        zoomIn={() => {
                          dispatch(isMapXL ? setDefaultZoom(ZoomActions.ZoomIn) : setMapZoom(ZoomActions.ZoomIn));
                          trackEvent({
                            category: 'Process',
                            action: 'Zoom in',
                            name: `${
                              isMapXL ? Math.round((defaultZoom + 0.2) * 100) : Math.round((mapZoom + 0.2) * 100)
                            }% - ${selectedProcess.name}`,
                          });
                        }}
                        zoomOut={() => {
                          dispatch(isMapXL ? setDefaultZoom(ZoomActions.ZoomOut) : setMapZoom(ZoomActions.ZoomOut));
                          trackEvent({
                            category: 'Process',
                            action: 'Zoom out',
                            name: `${
                              isMapXL ? Math.round((defaultZoom - 0.2) * 100) : Math.round((mapZoom - 0.2) * 100)
                            }% - ${selectedProcess.name}`,
                          });
                        }}
                        zoomLevel={isMapXL ? defaultZoom : mapZoom}
                      />
                      <FlowChartL2 subProcess={selectedSubProcess} zoomLevel={isMapXL ? defaultZoom : mapZoom} />
                    </TabPanel>
                  )}
                  <TabPanel
                    tabId={ProcessTabs.RACI}
                    index={selectedProcess?.diagram_link ? (isChangeNotificationsEnabled ? 2 : 1) : 0}
                    value={processTabIndex}
                    horizontalPadding={0}
                    backgroundColor='rgb(217, 217, 217, 0.2)'
                    maxWidth='100vw'
                    minWidth='98vw'
                  >
                    <SubProcessNavigation
                      data={getSubHeaderData()}
                      onL1Click={onL1Click}
                      selectedL1Label={selectedL1Label}
                      selectedL2Label={selectedL2Label}
                      onL2Click={onL2Click}
                      mode={ProcessTabs.RACI}
                      simplifiedCallback={setSimplified}
                      simplifiedRACI={selectedProcess?.simplified_raci ? simplifiedRACI : false}
                    />

                    <RACIView
                      onL1Click={onL1Click}
                      onL2Click={onL2Click}
                      handleTabChange={handleProcessPageTabChange}
                      simplified={selectedProcess?.simplified_raci ? simplifiedRACI : false}
                      processID={selectedProcess.process_id}
                      ttdom={selectedProcess?.operating_models_list === 'TTDOM'}
                    />
                  </TabPanel>
                  <TabPanel
                    tabId={ProcessTabs.Documents}
                    index={selectedProcess?.diagram_link ? (isChangeNotificationsEnabled ? 3 : 2) : 1}
                    value={processTabIndex}
                    maxWidth='99vw'
                    minWidth='98vw'
                  >
                    <DocAndTemplatesTab
                      process={selectedProcess}
                      storybook={false}
                      ttdom={selectedProcess?.operating_models_list === 'TTDOM'}
                      search={onSearchShortcut}
                      viewAllCallback={toArtifactsPage}
                    />
                  </TabPanel>
                  {/* <TabPanel
                    tabId={ProcessTabs.Learning}
                    index={4}
                    value={processTabIndex}
                    horizontalPadding={0}
                    backgroundColor='rgb(217, 217, 217, 0.2)'
                    maxWidth='100vw'
                    minWidth='98vw'
                  >
                    <Box p={6}>
                      <Typography variant='h1'>Add {ProcessTabs.Learning} Here</Typography>
                    </Box>
                  </TabPanel> */}
                </ProcessDetailsPage>
              </Box>
            )}
          </Stack>
        </PageLayout>
      ) : (
        <Box sx={{pt: 10, width: '100%'}}>
          <LinearProgress color='secondary' />
        </Box>
      )}
    </>
  );
};

export default ProcessPage;
